import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Group, Menu, Loader, Popover } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import Icon from "./icons";
import WeekPicker from "./WeekPicker";
import schedulerService from "../../../services/scheduler";
import { getAllStaff } from "../../../services/staff";
import { getAllPatientsModal } from "../../../services/patient";
import { getAllFacilitiesModal } from "../../../services/facility";
import WeekCalendar from "./WeekCalendar";
import GroupedWeekCalendar from "./GroupedWeekCalendar";
import MonthCalendar from "./MonthCalendar";
import CreateShiftDrawer, { SelectDD } from "./CreateShiftDrawer";
import "./SchedulerCreate.css";
import QuickBuildFab from "./QuickBuildFab";
import CopyWeekDrawer from "./CopyWeekDrawer";
import ShiftDrawer from "./ShiftDrawer";
import ShiftPopup from "./ShiftPopup";
import TimesheetsAdmin from "./TimesheetsAdmin";
import TimeOffPanel from "./TimeOffPanel";
import OpenShiftsView from "./OpenShiftsView";
import RosterView from "./RosterView";
import ExportPeriodModal from "./ExportPeriodModal";
import SchedulerSettingsPage from "./SchedulerSettingsPage";
import SetupWizard from "./SetupWizard";
import jsPDF from "jspdf";
import "jspdf-autotable";
import css from "./Scheduler.module.css";
import { startOfWeek, addDays, isoDate, weekLabel, MONTHS, entryMeta, fmtRange, shiftDiscrepancy } from "./scheduleUtils";
import { decrypt } from "../../../utils";

const idOf = (o) => o?._id || o?.id;
// Map a patient's billable service (form1.services entry) to a dropdown option.
const svcOpt = (s) => ({
  value: s.servicenumber || s.servicetitle,
  label: s.servicenumber ? `${s.servicetitle} (${s.servicenumber})` : s.servicetitle,
});
// Image fields (patient imageurl, staff photo) are stored AES-encrypted; only
// decrypt when the value is ciphertext so plain S3 URLs pass through untouched.
const photoOf = (v) => (typeof v === "string" && v.startsWith("U2FsdGVk") ? decrypt(v) : v || null);
// Normalise an id that may arrive raw or as a populated Mongoose doc, so we never
// stringify an object into "[object Object]" (which breaks ObjectId casts server-side).
const idStr = (v) => (v && typeof v === "object" ? String(v._id || v.id || v.value || "") : String(v ?? ""));

// Derive the calendar's visible time window from the configured operating hours.
// Any 24-hour facility => a full 24h span from the open time (so a 6 AM open
// runs through to 5 AM the next day). Otherwise earliest open -> latest close.
// Falls back to 6 AM–9 PM when no hours are configured.
const computeWindow = (facilityHours) => {
  const list = Array.isArray(facilityHours) ? facilityHours : [];
  if (!list.length) return { sMin: 6 * 60, eMin: 21 * 60 };
  const starts = list.filter((h) => !h.open24 && h.dayStartMin != null).map((h) => h.dayStartMin);
  const ends = list.filter((h) => !h.open24 && h.dayEndMin != null).map((h) => h.dayEndMin);
  const sMin = starts.length ? Math.min(...starts) : 6 * 60;
  if (list.some((h) => h.open24)) return { sMin, eMin: sMin + 24 * 60 };
  return { sMin, eMin: ends.length ? Math.max(...ends) : 21 * 60 };
};

export default function SchedulerAdmin() {
  const [adminView, setAdminView] = useState("schedule"); // 'schedule' | 'timesheets'
  const [view, setView] = useState("week");
  // "All" week layout: "time" (hourly grid) or "list" (no time breakdown)
  const [allView, setAllView] = useState("list");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [monthDate, setMonthDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [shifts, setShifts] = useState([]);
  // Approved + pending time-off for the visible range, shown on the calendar.
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedSettings, setSchedSettings] = useState(null);
  const calWindow = useMemo(() => computeWindow(schedSettings?.facilityHours), [schedSettings]);

  // Lookups come from the same redux-backed services the rest of the app uses.
  const allStaffRaw = useSelector((s) => s.staff?.allStaff);
  const allPatientsRaw = useSelector((s) => s.patient?.allPatients);
  const allFacilitiesRaw = useSelector((s) => s.facility?.allFacilities);

  // Standard app page header (title + notification + profile) used by every module.
  const { profile } = useSelector((s) => s?.profile || {});

  const staff = useMemo(
    () =>
      (allStaffRaw || [])
        .filter((x) => (x._id || x.id) && x.active === true && x.deleted !== true && x.type === "staff")
        .map((x) => ({
          value: x._id || x.id,
          label: x.name || `${x.firstName || ""} ${x.lastName || ""}`.trim(),
          photo: photoOf(x.photo || x.profilePicture || x.image || x.imageurl),
          facilityId: (x.facilityId || []).map(idStr).filter(Boolean),
        })),
    [allStaffRaw]
  );
  const facilities = useMemo(() => {
    const list = allFacilitiesRaw?.facilities || (Array.isArray(allFacilitiesRaw) ? allFacilitiesRaw : []);
    return list.filter((x) => (x._id || x.id) && x.active === true).map((x) => ({ value: x._id || x.id, label: x.name }));
  }, [allFacilitiesRaw]);
  const clients = useMemo(
    () =>
      (allPatientsRaw || [])
        .filter((x) => (x._id || x.id) && x.active === true && x.deleted !== true)
        .map((x) => ({
          value: x._id || x.id,
          label: x?.form1?.firstName ? `${x.form1.firstName} ${x.form1.lastName || ""}`.trim() : x.name || "Client",
          photo: photoOf(x.imageurl),
          // each client's own billable procedure/services (form1.services)
          services: (x?.form1?.services || []).filter((s) => s && s.servicetitle).map(svcOpt),
          facilityId: (x.facilityId || []).map(idStr).filter(Boolean),
          // staff explicitly assigned to this client (patient.staff[].staffId)
          staffIds: (x.staff || [])
            .filter((s) => s && s.isAssign !== false && s.staffId)
            .map((s) => idStr(s.staffId))
            .filter(Boolean),
        })),
    [allPatientsRaw]
  );

  // The business admin (logged-in owner) — schedulable like staff, shown at the
  // bottom of the quick-build drag list with an "Admin" pill.
  const admin = useMemo(() => {
    const u = profile?.user;
    const id = u && (u._id || u.id);
    if (!id) return null;
    return {
      value: id,
      label: u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Admin",
      photo: photoOf(u.photo || u.profilePicture || u.image || u.imageurl),
      isAdmin: true,
    };
  }, [profile]);

  // Assignable people = the owner plus staff. The owner (admin account) is
  // schedulable too, so any "assign to staff" picker includes them at the top.
  const assignable = useMemo(() => (admin ? [admin, ...staff] : staff), [admin, staff]);

  // Provider-wide fallback: the union of every client's billable services,
  // shown when no client is selected yet. Selected client narrows to its own.
  const services = useMemo(() => {
    const seen = new Set();
    const out = [];
    (allPatientsRaw || []).forEach((x) => {
      (x?.form1?.services || []).forEach((s) => {
        if (!s || !s.servicetitle) return;
        const o = svcOpt(s);
        if (seen.has(o.value)) return;
        seen.add(o.value);
        out.push(o);
      });
    });
    return out;
  }, [allPatientsRaw]);

  const [groupBy, setGroupBy] = useState("all");
  // Month view has no swimlanes, so the Facility tab exposes a single-facility
  // filter instead ("" = all facilities).
  const [monthFacility, setMonthFacility] = useState("");

  const [createKind, setCreateKind] = useState(null);

  // Quick-build: armed selection (staff + client/facility + shift) dragged onto days.
  const [quickArmed, setQuickArmed] = useState(null);
  const [quickPlaced, setQuickPlaced] = useState(0);
  // Preset for the create drawer when arriving from a client/facility "Add appointment".
  const [apptPreset, setApptPreset] = useState(null);
  const location = useLocation();
  useEffect(() => {
    const s = location.state;
    if (s?.openCreate === "appointment") {
      setApptPreset({ type: s.presetType, client: s.presetClient, facility: s.presetFacility });
      setCreateKind("appointment");
      // consume the nav state so it doesn't re-open on refresh/remount
      window.history.replaceState({}, "");
    } else if (s?.adminView === "timesheets") {
      // Deep-link into the Timesheets view (e.g. from the Staff page's "Manage Attendance").
      setAdminView("timesheets");
      window.history.replaceState({}, "");
    } else if (s?.focusShiftId) {
      // Deep-link from a Scheduler notification: jump to the shift's week.
      schedulerService.getShift(s.focusShiftId).then((shift) => {
        if (shift?.date) {
          setWeekStart(startOfWeek(new Date(shift.date)));
          setView("week");
        }
      }).catch(() => {});
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [detail, setDetail] = useState(null);
  const [editShift, setEditShift] = useState(null);
  const [hover, setHover] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [datePop, setDatePop] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  // Export flow: { format: 'csv' | 'pdf' } while the admin picks a date range.
  const [exportTarget, setExportTarget] = useState(null);
  // Count of pending time-off requests, for the Time Off tile.
  const [timeOffPending, setTimeOffPending] = useState(0);
  // Combined Time Off page sub-tab: 'timeoff' (default) | 'open' (open shifts).
  const [timeOffSubTab, setTimeOffSubTab] = useState("timeoff");
  useEffect(() => {
    schedulerService.getTimeOff({ status: "pending" }).then((l) => setTimeOffPending((l || []).length)).catch(() => {});
  }, [adminView]);
  const [rosterMode, setRosterMode] = useState(false);
  // True when viewing a week earlier than the current one — enables "copy this
  // week's schedule into a future week" from the date nav.
  const isPastWeek = view === "week" && startOfWeek(weekStart).getTime() < startOfWeek(new Date()).getTime();
  const [dpMonth, setDpMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const range = useMemo(() => {
    if (view === "week") return { from: weekStart, to: addDays(weekStart, 6) };
    const gridStart = startOfWeek(monthDate);
    return { from: gridStart, to: addDays(gridStart, 41) };
  }, [view, weekStart, monthDate]);

  const loadShifts = useCallback(async () => {
    setLoading(true);
    const data = await schedulerService.getShifts({ from: isoDate(range.from), to: isoDate(range.to) });
    setShifts(data || []);
    const off = await schedulerService.getTimeOff({ from: isoDate(range.from), to: isoDate(range.to) });
    setTimeOff((off || []).filter((t) => t.status === "approved" || t.status === "pending"));
    setLoading(false);
  }, [range.from, range.to]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  useEffect(() => {
    if (!staff.length) getAllStaff("all", false, undefined, "");
    if (!clients.length) getAllPatientsModal("all", false);
    if (!facilities.length) getAllFacilitiesModal("all", false);
    schedulerService.getSchedulerSettings().then((s) => setSchedSettings(s || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleShifts = shifts;
  // Shifts for the month grid: on the Facility tab a chosen facility narrows the
  // month to that facility (otherwise everything shows).
  const monthShifts =
    groupBy === "facility" && monthFacility
      ? visibleShifts.filter((s) => idStr(s.facility) === monthFacility)
      : visibleShifts;

  // Quick-build drop: create a shift on `day` from the armed selection, using the
  // chosen shift template's times. Stays armed so more days can be filled.
  const handleQuickDrop = useCallback(
    async (day, payload) => {
      const a = payload;
      // staff is required for shifts but null for appointments
      if (!a || (a.kind !== "appointment" && !a.staffId)) return;
      // createShift resolves to the created record on success, or undefined on a
      // rejected request — only count/reload when it actually saved.
      const created = await schedulerService.createShift({
        type: a.type,
        kind: a.kind || "shift",
        apptTitle: a.apptTitle || "",
        facility: a.facilityId || null,
        staff: a.staffId || null,
        client: a.clientId || null,
        service: "",
        date: `${isoDate(day)}T12:00:00`,
        startMin: a.startMin,
        endMin: a.endMin,
        allDay: !!a.allDay,
        recurrence: { freq: "none" },
      });
      if (!created) return;
      setQuickPlaced((n) => n + 1);
      loadShifts();
    },
    [loadShifts]
  );

  // Quick-build appointment with an explicit date+time: place it directly.
  const handleQuickPlace = useCallback(
    async (payload, dateStr) => {
      if (!payload || !dateStr) return;
      const created = await schedulerService.createShift({
        type: payload.type,
        kind: payload.kind || "appointment",
        apptTitle: payload.apptTitle || "",
        facility: payload.facilityId || null,
        staff: payload.staffId || null,
        client: payload.clientId || null,
        service: "",
        date: `${dateStr}T12:00:00`,
        startMin: payload.startMin,
        endMin: payload.endMin,
        allDay: !!payload.allDay,
        recurrence: { freq: "none" },
      });
      if (!created) return;
      setQuickPlaced((n) => n + 1);
      loadShifts();
    },
    [loadShifts]
  );

  // Drag an existing shift onto another day to reschedule it (keeps its time).
  const handleShiftMove = useCallback(
    async (shift, day) => {
      if (!shift) return;
      const id = shift._id || shift.id;
      const current = shift.date ? isoDate(new Date(shift.date)) : null;
      const target = isoDate(day);
      if (current === target) return; // dropped on the same day
      await schedulerService.updateShift(id, { date: target });
      loadShifts();
    },
    [loadShifts]
  );

  const kpis = useMemo(() => {
    const base = view === "week" ? shifts : shifts; // shifts already scoped to the visible range
    let scheduledHrs = 0;
    let open = 0;
    let urgent = 0;
    let exceptions = 0;
    base.forEach((s) => {
      if (s.status === "open") {
        open += 1;
        if (s.open?.urgent) urgent += 1;
      } else if (s.kind !== "appointment" && s.staff && !s.allDay && s.endMin > s.startMin) {
        // Scheduled hours = assigned staff shift hours only (exclude appointments and open shifts).
        scheduledHrs += (s.endMin - s.startMin) / 60;
      }
      // A late clock-in or a missed shift (no-show) is a discrepancy the admin
      // should review. Old code only counted status==="exception", which the
      // attendance derivation never sets, so the tile always read 0.
      if (shiftDiscrepancy(s)) exceptions += 1;
    });
    return { scheduledHrs: Math.round(scheduledHrs), open, urgent, exceptions };
  }, [shifts, view]);

  const openDetail = async (shift) => {
    setHover(null);
    // Virtual open shifts (generated from schedules) have no DB doc to fetch —
    // open them directly; filling one creates a real shift from the slot.
    if (shift?.virtual) {
      setDetail(shift);
      return;
    }
    const full = await schedulerService.getShift(idOf(shift));
    setDetail(full || shift);
  };
  const onHover = (s, e) => setHover({ shift: s, x: e.clientX, y: e.clientY });
  const onLeave = () => setHover(null);

  const downloadCsv = async (fromISO, toISO) => {
    const blob = await schedulerService.exportSchedule({ from: fromISO, to: toISO });
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `schedule-${fromISO}_${toISO}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = (shiftsArr, labelText, fileTag) => {
    const doc = new jsPDF();
    doc.setFontSize(13);
    const bizName = profile?.business?.companyName;
    doc.text(`${bizName ? `${bizName} · ` : ""}Schedule · ${labelText}`, 14, 16);
    const sorted = [...shiftsArr].sort((a, b) => (a.facility?.name || "").localeCompare(b.facility?.name || "") || new Date(a.date) - new Date(b.date) || a.startMin - b.startMin);
    const body = sorted.map((s) => [
      new Date(s.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
      s.facility?.name || "",
      s.staff?.name || "Open",
      entryMeta(s).pill,
      s.clientName || s.client?.name || "",
      s.allDay ? "All day" : fmtRange(s.startMin, s.endMin),
    ]);
    doc.autoTable({
      head: [["Date", "Facility", "Staff", "Type", "Client", "Time"]],
      body,
      startY: 22,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [23, 23, 23] },
    });
    doc.save(`roster-${fileTag}.pdf`);
  };

  // Export the schedule for an admin-chosen date range. CSV pulls the range
  // straight from the API; PDF fetches the range's shifts too, so neither is
  // limited to the week currently on screen.
  const runScheduleExport = async ([from, to]) => {
    const fromISO = from.format ? from.format("YYYY-MM-DD") : isoDate(from);
    const toISO = to.format ? to.format("YYYY-MM-DD") : isoDate(to);
    const fmt = exportTarget?.format;
    setExportTarget(null);
    if (fmt === "csv") {
      await downloadCsv(fromISO, toISO);
    } else {
      const rangeShifts = await schedulerService.getAppointments({ from: fromISO, to: toISO });
      downloadPdf(rangeShifts || [], `${fromISO} – ${toISO}`, `${fromISO}_${toISO}`);
    }
  };

  const navPrev = () => (view === "week" ? setWeekStart(addDays(weekStart, -7)) : setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1)));
  const navNext = () => (view === "week" ? setWeekStart(addDays(weekStart, 7)) : setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)));
  const goToDate = (d) => {
    if (!d) return;
    setWeekStart(startOfWeek(d));
    setMonthDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setDatePop(false);
  };
  const openPicker = () => {
    const ref = view === "week" ? weekStart : monthDate;
    setDpMonth(new Date(ref.getFullYear(), ref.getMonth(), 1));
    setDatePop((o) => !o);
  };
  const label = view === "week" ? weekLabel(weekStart) : `${MONTHS[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  // Top action toolbar (Create + More actions / settings). Shared by
  // the Schedule, Timesheets, and Open Shifts views.
  const pageActionsBar = (
    <div className={css.pageActions}>
      <div className={css.pageActionsL}>
        <Menu shadow="md" width={248} position="bottom-start">
          <Menu.Target>
            <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`}>
              <Icon name="plus" size={16} /> Add <Icon name="chevronDown" size={13} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => setCreateKind("schedule")}>
              <div className={css.cmItem}>
                <span className={css.cmIc} style={{ background: "var(--app-tint-blue)", color: "var(--app-ink-blue)" }}><Icon name="user" size={17} /></span>
                <span>
                  <span className={css.cmT}>Shift</span>
                </span>
              </div>
            </Menu.Item>
            <Menu.Item onClick={() => setCreateKind("appointment")}>
              <div className={css.cmItem}>
                <span className={css.cmIc} style={{ background: "var(--app-tint-purple)", color: "var(--app-ink-purple)" }}><Icon name="calendar" size={17} /></span>
                <span>
                  <span className={css.cmT}>Appointment</span>
                </span>
              </div>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <Group spacing={8}>
        <button type="button" className={css.iconBtnSquare} onClick={() => setAdminView("settings")} aria-label="Scheduler settings" title="Scheduler settings">
          <Icon name="gear" size={18} />
        </button>
      </Group>
    </div>
  );

  // Drawers the toolbar buttons open — mounted in every view that shows the bar.
  const schedulerDrawers = (
    <>
      <CreateShiftDrawer
        opened={!!createKind}
        kind={createKind || "schedule"}
        onClose={() => { setCreateKind(null); setApptPreset(null); }}
        onCreated={loadShifts}
        facilities={facilities}
        staff={assignable}
        clients={clients}
        services={services}
        shifts={visibleShifts}
        defaultDate={new Date()}
        presetType={apptPreset?.type}
        presetClient={apptPreset?.client}
        presetFacility={apptPreset?.facility}
      />
      <ShiftDrawer
        opened={!!detail}
        shift={detail}
        onClose={() => setDetail(null)}
        onChanged={loadShifts}
        onEdit={(s) => { setDetail(null); setEditShift(s); }}
        staffOptions={assignable}
      />
      <CreateShiftDrawer
        opened={!!editShift}
        editShift={editShift}
        onClose={() => setEditShift(null)}
        onCreated={loadShifts}
        facilities={facilities}
        staff={assignable}
        clients={clients}
        services={services}
        shifts={visibleShifts}
      />
      <CopyWeekDrawer
        opened={copyOpen}
        onClose={() => setCopyOpen(false)}
        sourceWeekStart={weekStart}
        shifts={visibleShifts}
        staff={staff}
        onCopied={(target) => {
          // Jump to the target week so the admin sees the copied schedule.
          if (target) setWeekStart(startOfWeek(target));
          loadShifts();
        }}
      />
    </>
  );

  if (adminView === "timesheets") {
    return (
      <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", minHeight: "100vh", background: "var(--app-bg)" }}>
        <AppNavbar title="Calendar" profile={profile} />
        <Box pt={24} px={20} pb={20}>
          <button type="button" className={css.backNav} onClick={() => setAdminView("schedule")}>
            <span className={css.bnIc}><Icon name="chevronLeft" size={15} /></span>
            Back to Calendar
          </button>
          {pageActionsBar}
          <TimesheetsAdmin />
        </Box>
        {schedulerDrawers}
      </Box>
    );
  }

  // Time Off + Open Shifts — one page, two tabs (Time-off requests is the default
  // tab, Open shifts is second). The single dashboard tile opens here. Tabs live
  // in the panel's card header via the shared `tabs` prop (same pattern as staff).
  if (adminView === "timeoff") {
    // Header for the section card: tabs on the left, the weekly navigation on the
    // right — the whole view is grounded on the selected week, so its nav lives
    // inside the section rather than floating above it.
    const timeOffTabs = (
      <>
        <div style={{ display: "flex", gap: 6, background: "var(--app-surface-2)", padding: 4, borderRadius: 8, width: "fit-content" }}>
          {[["timeoff", "Time-off request"], ["open", "Open shift"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTimeOffSubTab(k)} style={{ padding: "6px 14px", borderRadius: 6, border: 0, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", color: "var(--app-text)", background: timeOffSubTab === k ? "var(--app-surface)" : "transparent", boxShadow: timeOffSubTab === k ? "0 1px 2px rgba(16,24,40,0.06)" : "none" }}>{l}</button>
          ))}
        </div>
        <div className={css.dateNav}>
          <button type="button" className={css.navArrow} onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week"><Icon name="chevronLeft" size={15} /></button>
          <span className={css.dateLabel2} style={{ cursor: "default" }}>{weekLabel(weekStart)}</span>
          <button type="button" className={css.navArrow} onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week"><Icon name="chevron" size={15} /></button>
        </div>
      </>
    );
    return (
      <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", minHeight: "100vh", background: "var(--app-bg)" }}>
        <AppNavbar title="Calendar" profile={profile} />
        <Box pt={24} px={20} pb={20}>
          <button type="button" className={css.backNav} onClick={() => setAdminView("schedule")}>
            <span className={css.bnIc}><Icon name="chevronLeft" size={15} /></span>
            Back to Calendar
          </button>
          {pageActionsBar}
          {timeOffSubTab === "open" ? (
            <OpenShiftsView tabs={timeOffTabs} from={weekStart} to={addDays(weekStart, 6)} onRowClick={openDetail} onPost={() => setCreateKind("schedule")} />
          ) : (
            <TimeOffPanel tabs={timeOffTabs} mode="admin" onCountChange={setTimeOffPending} from={isoDate(weekStart)} to={isoDate(addDays(weekStart, 6))} />
          )}
        </Box>
        {schedulerDrawers}
      </Box>
    );
  }

  if (adminView === "settings") {
    return (
      <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", minHeight: "100vh", background: "var(--app-bg)" }}>
        <AppNavbar title="Calendar" profile={profile} />
        <Box pt={24} px={20} pb={20}>
          <SchedulerSettingsPage facilities={facilities} onBack={() => setAdminView("schedule")} onResetSetup={() => setSettingsOpen(true)} />
        </Box>
        <SetupWizard opened={settingsOpen} onClose={() => setSettingsOpen(false)} onComplete={() => setSettingsOpen(false)} facilities={facilities} />
      </Box>
    );
  }

  const GROUPS = [
    ["all", "All"],
    ["staff", "Staff"],
    ["client", "Clients"],
    ["facility", "Facility"],
  ];

  const dateNavEl = (
    <div className={css.dateNav}>
      <button type="button" className={css.navArrow} onClick={navPrev} aria-label="Previous"><Icon name="chevronLeft" size={15} /></button>
      <Popover opened={datePop} onChange={setDatePop} position="bottom" withinPortal shadow="md" radius={16}>
        <Popover.Target>
          <button type="button" className={css.dateLabel2} onClick={openPicker} aria-haspopup="dialog">{label}</button>
        </Popover.Target>
        <Popover.Dropdown p={0}>
          <WeekPicker
            monthDate={dpMonth}
            selWeek={startOfWeek(weekStart)}
            mode={view === "month" ? "month" : "week"}
            onMonthChange={setDpMonth}
            onPick={goToDate}
            onClose={() => setDatePop(false)}
          />
        </Popover.Dropdown>
      </Popover>
      <button type="button" className={css.navArrow} onClick={navNext} aria-label="Next"><Icon name="chevron" size={15} /></button>
      {isPastWeek ? (
        <button type="button" className={`${css.navArrow} ${css.navToday}`} onClick={() => setCopyOpen(true)} aria-label="Copy this week's schedule" title="Copy this week's schedule to another week"><Icon name="copy" size={16} /></button>
      ) : (
        <button type="button" className={`${css.navArrow} ${css.navToday}`} onClick={openPicker} aria-label="Pick a date" title="Pick a date"><Icon name="calendarDot" size={16} /></button>
      )}
    </div>
  );

  return (
    <Box style={{ flex: 1, minWidth: 0, maxWidth: "100%", minHeight: "100vh", background: "var(--app-bg)" }}>
      <AppNavbar title="Calendar" profile={profile} />
      <Box pt={24} px={20} pb={20}>
      {pageActionsBar}

      <div className={css.kpiGrid}>
        <div className={css.kpi}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Scheduled hours</span>
            <span className={`${css.kpiIcon} ${css.info}`}><Icon name="clock" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{kpis.scheduledHrs}<span className={css.kpiUnit}>hrs</span></div>
          <div className={css.kpiMeta}>{view === "week" ? "this week" : "this month"}</div>
        </div>
        <div className={`${css.kpi} ${css.kpiClick}`} role="button" tabIndex={0} onClick={() => setAdminView("timesheets")}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Timesheets</span>
            <span className={`${css.kpiIcon} ${css.green}`}><Icon name="clock" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{kpis.exceptions}</div>
          <div className={css.kpiMeta}>{kpis.exceptions ? "need review" : "all clear"}</div>
        </div>
        <div className={`${css.kpi} ${css.kpiClick}`} role="button" tabIndex={0} onClick={() => { setTimeOffSubTab("timeoff"); setAdminView("timeoff"); }}>
          <div className={css.kpiRow}>
            <span className={css.kpiLabel}>Time-off request</span>
            <span className={`${css.kpiIcon} ${css.violet}`}><Icon name="calendar" size={17} /></span>
          </div>
          <div className={css.kpiValue}>{timeOffPending}</div>
          <div className={css.kpiMeta}><strong>{kpis.open}</strong> open shift{kpis.open === 1 ? "" : "s"}</div>
        </div>
      </div>

      <div className={css.calCard}>
        {rosterMode ? (
          <div className={css.calModebar}>
            <button type="button" className={css.tbBtn} onClick={() => setRosterMode(false)}>
              <Icon name="chevronLeft" size={15} /> Back to Calendar
            </button>
            <div className={css.spacer} />
            {dateNavEl}
            <div className={css.spacer} />
          </div>
        ) : (
        <div className={css.calModebar}>
          <div className={css.seg}>
            {GROUPS.map(([v, l]) => (
              <button
                key={v}
                type="button"
                className={`${css.segBtn} ${groupBy === v ? css.segActive : ""}`}
                onClick={() => setGroupBy(v)}
              >
                {l}
              </button>
            ))}
          </div>
          {groupBy === "all" && view === "week" && (
            <button
              type="button"
              className={`${css.calToolBtn} ${allView === "time" ? css.calToolBtnOn : ""}`}
              title={allView === "time" ? "Showing times. Click to hide the time breakdown" : "No time breakdown. Click to show times"}
              aria-label="Toggle time-of-day view"
              aria-pressed={allView === "time"}
              onClick={() => setAllView(allView === "time" ? "list" : "time")}
            >
              <Icon name={allView === "time" ? "alarm" : "alarmOff"} size={17} />
            </button>
          )}
          {groupBy === "facility" && view === "month" && (
            <div className="sd-create" style={{ minWidth: 200 }}>
              <SelectDD
                placeholder="All facilities"
                value={monthFacility}
                options={[{ value: "", label: "All facilities" }, ...facilities]}
                onChange={(v) => setMonthFacility(v || "")}
              />
            </div>
          )}

          <div className={css.spacer} />

          {dateNavEl}

          <div className={css.spacer} />

          <div className={css.seg}>
            {[["week", "Week"], ["month", "Month"]].map(([v, l]) => (
              <button key={v} type="button" className={`${css.segBtn} ${view === v ? css.segActive : ""}`} onClick={() => setView(v)}>
                {l}
              </button>
            ))}
          </div>
          <button type="button" className={css.calToolBtn} title="Open roster" aria-label="Open roster" onClick={() => setRosterMode(true)}>
            <Icon name="download" size={17} />
          </button>
        </div>
        )}

        {rosterMode ? (
          <div className={css.rosterCanvas}>
            <RosterView shifts={shifts} weekStart={weekStart} businessName={profile?.business?.companyName} logo={profile?.business?.logo} onCsv={() => setExportTarget({ format: "csv" })} onPdf={() => setExportTarget({ format: "pdf" })} />
          </div>
        ) : loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 420, padding: 60 }}>
            <Loader color="var(--app-primary)" />
          </div>
        ) : view === "week" ? (
          groupBy === "all" ? (
            allView === "time" ? (
              <WeekCalendar shifts={visibleShifts} timeOff={timeOff} weekStart={weekStart} windowStart={calWindow.sMin} windowEnd={calWindow.eMin} onShiftClick={openDetail} onHover={onHover} onLeave={onLeave} dropArmed={!!quickArmed} onDayDrop={handleQuickDrop} onShiftMove={handleShiftMove} />
            ) : (
              <GroupedWeekCalendar shifts={visibleShifts} timeOff={timeOff} weekStart={weekStart} groupBy="all" staff={staff} facilities={facilities} clients={clients} onShiftClick={openDetail} onHover={onHover} onLeave={onLeave} dropArmed={!!quickArmed} onDayDrop={handleQuickDrop} />
            )
          ) : (
            <GroupedWeekCalendar
              shifts={shifts}
              timeOff={timeOff}
              weekStart={weekStart}
              groupBy={groupBy}
              staff={staff}
              facilities={facilities}
              clients={clients}
              onShiftClick={openDetail}
              onHover={onHover}
              onLeave={onLeave}
              dropArmed={!!quickArmed}
              onDayDrop={handleQuickDrop}
            />
          )
        ) : (
          <MonthCalendar shifts={monthShifts} timeOff={timeOff} monthDate={monthDate} onShiftClick={openDetail} onHover={onHover} onLeave={onLeave} />
        )}
      </div>
      </Box>

      {hover && <ShiftPopup shift={hover.shift} x={hover.x} y={hover.y} />}

      {/* Quick-build FAB — week view only, while not in roster mode */}
      {view === "week" && !rosterMode && (
        <QuickBuildFab
          staff={staff}
          admin={admin}
          clients={clients}
          facilities={facilities}
          placedCount={quickPlaced}
          onArmChange={setQuickArmed}
          onPlaceNow={handleQuickPlace}
        />
      )}

      {schedulerDrawers}

      <ExportPeriodModal
        opened={!!exportTarget}
        target={exportTarget ? { kind: "schedule", format: exportTarget.format } : null}
        defaultStart={weekStart}
        onConfirm={runScheduleExport}
        onClose={() => setExportTarget(null)}
      />

      <SetupWizard opened={settingsOpen} onClose={() => setSettingsOpen(false)} onComplete={() => setSettingsOpen(false)} />
    </Box>
  );
}
